import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Form} from "antd";
import {
  canPerformAction,
  StepKeysName,
  StepNames,
} from "../../../RaiseNoticeAdRequest/utils";
import moment from "moment";
import EmailForm from "../EmailForm";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getCitiesByStateAction,
  getQuotationDataAction,
  postQuotationDataAction,
  saveQuoteFormDataAction,
  sendEmailAction,
} from "../../../../store/actions/raiseAdRequest";
import { RaiseAdRequestState } from "../../../../store/types/raiseAdRequest";
import { openToast } from "../../../../Toasts";


const handleDateDisabled = (current: any) => {
  return moment().diff(current, "days") > 0;
};
const permittedRoles = ["admin", "only-approver", "product-admin"];
export const RequestCreationEmailForm: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {
    quoationApprovalFormData,
    states,
    cities,
    userRole,
    lastStepName,
  } = useSelector(
    (state: { raiseAdRequest: RaiseAdRequestState }) => state.raiseAdRequest
  );

  const isStepAccessible = canPerformAction(
    userRole,
    permittedRoles,
    StepNames[0],
    lastStepName,
    quoationApprovalFormData.trashed_at
  );
  const { requestId, selectedProductId } = useParams<{
    requestId: string;
    selectedProductId: string;
  }>();

  const runCount = useRef(0);
  const [isFormValuesChanged, setIsFormValuesChanged] = useState<boolean>(
    false
  );
  const [filedValues, setFieldValues] = useState<any>();
  const [emailFormValues, setEmailFormValues] = useState({
    to: "",
    cc: "",
    message: "",
  });

  useEffect(() => {
    dispatch(getQuotationDataAction(requestId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isStepAccessible && !!userRole) {
      openToast("info", "You only have view access to this information.");
    }
  }, [isStepAccessible, userRole]);

  // ✅ Populate form when data is available
  useEffect(() => {
    if (quoationApprovalFormData) {
      form.setFieldsValue({
        ...quoationApprovalFormData,
        state: quoationApprovalFormData.state || undefined,
        city: quoationApprovalFormData.city || undefined,
        final_release_date: !!quoationApprovalFormData.final_release_date
          ? moment(quoationApprovalFormData.final_release_date)
          : null,
        length:
          (!!quoationApprovalFormData.length
            ? quoationApprovalFormData.length
            : undefined) ?? undefined,
        width:
          (!!quoationApprovalFormData.width
            ? quoationApprovalFormData.width
            : undefined) ?? undefined,

        emailContent: {
          ...form.getFieldValue("emailContent"),
          ...quoationApprovalFormData.emailContent,
        },
      });

    }
  }, [
    form,
    quoationApprovalFormData,
    filedValues,
    requestId,
    selectedProductId,
  ]);
  useEffect(() => {
    if (quoationApprovalFormData && runCount.current < 3) {
      setEmailFormValues({
        to: quoationApprovalFormData?.emailContent?.to,
        cc: quoationApprovalFormData?.emailContent?.cc,
        message: quoationApprovalFormData?.emailContent?.message,
      });

      runCount.current += 1; // ✅ increment counter
    }
  }, [quoationApprovalFormData]);

  const handleSelectState = useCallback(
    (value) => {
      const requiredState = states.find((state) => state.name === value);
      dispatch(getCitiesByStateAction(requiredState.id));
    },
    [dispatch, states]
  );

  const onFinish = useCallback(
    (values: any) => {
      // Destructure otherCity out of values
      const { otherCity, otherRequestType, ...rest } = quoationApprovalFormData;

      let finalValues = { ...rest };

      if (otherCity) {
        finalValues.city = otherCity;
      }

      if (otherRequestType) {
        finalValues.request_type = otherRequestType;
      }

      if (values.creative_size) {
        const match = values.creative_size.match(/(\d+)\s*[xX]\s*(\d+)/);
        if (match) {
          const [, length, width] = match;
          finalValues = {
            ...finalValues,
            length: Number(length),
            width: Number(width),
          };
        }
      }

      if (!!isFormValuesChanged) {
        dispatch(
                  postQuotationDataAction({
                    id: requestId,
                    formData: finalValues,
                    stage: "Agency Quote Details",
                    nextStepKey: StepKeysName.AgencyQuoteDetails,
                    workspaceId: selectedProductId,
                    emailFormValues,
                  })
                );
      }
    },
    [
      dispatch,
      emailFormValues,
      isFormValuesChanged,
      requestId,
      selectedProductId,
    ]
  );

  return (
    <>
      <div className="layout-wrapper">
        {/* Scrollable Form */}
        <div className="layout-wrapper__body">
          <Form
            form={form}
            onFinish={onFinish}
            className="custom-form"
            onValuesChange={(changedValues, allValues) => {
              if (!!isStepAccessible) {
                setIsFormValuesChanged(true);
                if (changedValues.emailContent) {
                  const changedKey = Object.keys(changedValues.emailContent)[0];
                  const changedValue = changedValues.emailContent[changedKey];

                  setEmailFormValues((prev) => ({
                    ...prev,
                    [changedKey]: changedValue, // ✅ update only that field
                  }));
                }
                // Only dispatch if the changed field is NOT emailContent
                if (Object.keys(changedValues)?.[0] !== "emailContent") {
                  setFieldValues(changedValues);
                  dispatch(saveQuoteFormDataAction(changedValues));
                }
              }
            }}
          >
            <div
              style={{
                margin: "2rem 0",
                display: "flex",
                gap: "2rem",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 600,
                  color: "#394455",
                }}
              >
                Send Email to Agency
              </div>
              <EmailForm
                form={form}
                fixedBody={quoationApprovalFormData?.emailContent?.fixedBody}
                footerMessage={
                  quoationApprovalFormData?.emailContent?.footerMessage
                }
                subject={quoationApprovalFormData?.emailContent?.subject}
                emailFormValues={emailFormValues}
                setEmailFormValues={setEmailFormValues}
                isUserPermitted={isStepAccessible}
                setIsFormValuesChanged={setIsFormValuesChanged}
                isCcEditable={false}
              />
            </div>
          </Form>
        </div>
        {!!isStepAccessible ? (
          <div className="layout-wrapper__footer">
            <button
              className="btn btn--purple"
              disabled={
                !isFormValuesChanged || !emailFormValues?.to?.trim().length
              }
              onClick={() => form.submit()}
            >
              Request Quote
            </button>
          </div>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
